import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  getSettingList,
  defaultPersonalised,
  getPersonalisedConsent,
} from '@/intl-jdi-common-ssr/constants/cookie';
import { Switch } from '@mui/material';
import { SwitchProps } from '@mui/material/Switch';
import { styled } from '@mui/material/styles';
import cookieUtils from '@/intl-jdi-common-ssr/utils/cookieUtils';
import { getOrSetSwitches } from '@/apis/root/root.api';
import { Item } from '@/apis/root/root.interface';
import { useTranslation } from '@/i18n/client';
import styles from './index.module.scss';

interface SettingListProps {
  isLogin: boolean;
}

interface Personalised {
  [key: string]: number | string | boolean;
}

interface SettingListMethods {
  getPersonalised: () => Personalised;
}

const SettingList = forwardRef<SettingListMethods, SettingListProps>(({ isLogin }, ref) => {
  const [personalised, setPersonalised] = useState<Personalised>(defaultPersonalised);
  const { t } = useTranslation();
  const PersonalisedSettingList = getSettingList(t).PersonalisedSettingList;

  useEffect(() => {
    getPersonalisedRoot();
  }, []);

  const getPersonalisedRoot = async () => {
    if (isLogin) {
      const { code, data = [] } = await getOrSetSwitches({
        scenes: [{ scene: 0, switchStatus: null }],
      });
      if (code === '200') {
        const newPersonalised = JSON.parse(JSON.stringify(personalised));
        data.map((item: Item, index: number) => {
          newPersonalised[PersonalisedSettingList[index].key] = Number(item.switchStatus);
        });
        setPersonalised(newPersonalised);
      }
    } else {
      const personalisedConsent = getPersonalisedConsent();
      setPersonalised(personalisedConsent);
    }
  };

  const handleSwitchChange = async (checked: boolean, item: Personalised) => {
    const newUserCookie = { ...personalised, ...{ [item.key as string]: Number(checked) } };
    setPersonalised(newUserCookie);
  };

  useImperativeHandle(ref, () => ({
    getPersonalised: () => {
      return personalised;
    },
  }));

  return (
    <div className={styles.cookiesSettingWrapper}>
      {PersonalisedSettingList.map((item) => (
        <div key={item.key}>
          <div className={styles.title}>
            {item.title}
            {item.isControl ? (
              <CustomerSwitch
                checked={Boolean(personalised[item.key])}
                onChange={(e) => handleSwitchChange(e.target.checked, item)}
              />
            ) : (
              <span className={styles.status}>{t('personalised.alwaysActive')}</span>
            )}
          </div>
          <div className={styles.content}>{item.content}</div>
        </div>
      ))}
    </div>
  );
});

const CustomerSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName='.Mui-focusVisible' disableRipple {...props} />
))(({ theme }) => ({
  width: 48,
  height: 28,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 3,
    margin: 0,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      // transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#1A1A1A',
        opacity: 1,
        border: 0,
        ...theme.applyStyles('dark', {
          backgroundColor: '#1A1A1A',
        }),
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.12,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#1A1A1A',
      border: '3px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: '#fff',
      ...theme.applyStyles('dark', {
        color: theme.palette.grey[600],
      }),
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.38,
      ...theme.applyStyles('dark', {
        opacity: 0.3,
      }),
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#C2C4CC',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
    ...theme.applyStyles('dark', {
      backgroundColor: '#39393D',
    }),
  },
}));
SettingList.displayName = 'SettingList';
export default SettingList;
